import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ClipboardList, AlertTriangle, Search, RotateCcw, CheckCircle, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { adminAPI } from '../services/api';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
  max-width: 720px;
`;

const Panel = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  box-shadow: ${props => props.theme.shadows.sm};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.background};
  border: 1px dashed ${props => props.theme.colors.borderMuted};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
  justify-content: space-between;
`;

const SearchInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.surfaceMuted};
  flex: 1;
  max-width: 320px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;

  &:focus {
    outline: none;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${props => props.theme.colors.surfaceMuted};
  font-weight: 600;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.text};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${props =>
    props.status === 'active'
      ? 'rgba(16, 185, 129, 0.15)'
      : props.status === 'canceled'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(148, 163, 184, 0.15)'};
  color: ${props =>
    props.status === 'active'
      ? '#059669'
      : props.status === 'canceled'
      ? '#DC2626'
      : '#475569'};
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: ${props => props.theme.borderRadius.sm};
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.surfaceMuted};
  }

  &.danger {
    border-color: ${props => props.theme.colors.error};
    color: ${props => props.theme.colors.error};

    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px dashed ${props => props.theme.colors.border};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const PaginationButton = styled.button`
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  padding: 8px 12px;
  border-radius: ${props => props.theme.borderRadius.sm};
  cursor: pointer;
  min-width: 120px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function BillingSubscriptions() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading, error, refetch } = useQuery(
    ['admin.billing.subscriptions', statusFilter, search, page],
    () =>
      adminAPI
        .getBillingSubscriptions({
          status: statusFilter || undefined,
          search: search || undefined,
          limit: 20,
          offset: page * 20,
        })
        .then(res => res.data),
    {
      keepPreviousData: true,
      refetchInterval: 60000,
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status, reason }) =>
      adminAPI.updateBillingSubscriptionStatus(id, status, reason).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('Статус подписки обновлён');
        queryClient.invalidateQueries('admin.billing.subscriptions');
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось обновить подписку');
      },
    }
  );

  const subscriptions = useMemo(() => data?.subscriptions ?? [], [data]);
  const total = data?.total ?? 0;
  const hasNext = (page + 1) * 20 < total;
  const isBusy = updateStatusMutation.isLoading;

  const handleCancel = sub => {
    if (window.confirm('Отменить подписку пользователя?')) {
      updateStatusMutation.mutate({
        id: sub.id,
        status: 'canceled',
        reason: 'Отмена из админки',
      });
    }
  };

  const handleReactivate = sub => {
    updateStatusMutation.mutate({
      id: sub.id,
      status: 'active',
      reason: 'Возврат в активный статус из админки',
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <ClipboardList size={32} />
          Подписки пользователей
        </Title>
        <Subtitle>
          Просматривайте активные подписки, ищите пользователей по TG ID или ключу тарифа и быстро
          отменяйте или повторно активируйте планы.
        </Subtitle>
      </Header>

      <Panel>
        <InfoBox>
          <AlertTriangle size={18} />
          <div>
            <strong>Автопродление.</strong> Поле auto_renew отображает настройку пользователя. При
            ручном переводе в статус <code>canceled</code> следует уточнять у клиента, нужно ли
            возвращать оставшиеся средства.
          </div>
        </InfoBox>

        <Controls>
          <SearchInputWrap>
            <Search size={16} color="#64748b" />
            <SearchInput
              value={search}
              placeholder="Поиск по TG ID, ключу плана..."
              onChange={e => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
          </SearchInputWrap>

          <FilterSelect
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <option value="">Все статусы</option>
            <option value="active">Активные</option>
            <option value="canceled">Отменённые</option>
            <option value="paused">Пауза</option>
            <option value="expired">Истёкшие</option>
          </FilterSelect>

          <GhostButton onClick={() => refetch()} disabled={isBusy}>
            <RotateCcw size={16} />
            Обновить
          </GhostButton>
        </Controls>

        {isLoading ? (
          <EmptyState>Загрузка подписок...</EmptyState>
        ) : error ? (
          <EmptyState>
            <AlertTriangle size={18} /> Не удалось загрузить список подписок
          </EmptyState>
        ) : subscriptions.length === 0 ? (
          <EmptyState>Подписки не найдены</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Пользователь</Th>
                  <Th>План</Th>
                  <Th>Статус</Th>
                  <Th>Период</Th>
                  <Th>Автообновление</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr key={sub.id}>
                    <Td>
                      <div style={{ fontWeight: 600 }}>
                        @{sub.username || 'без_username'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        TG: {sub.user_tg_id || '—'}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{sub.plan_name || '—'}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {sub.plan_key || 'нет ключа'}
                      </div>
                    </Td>
                    <Td>
                      <StatusBadge status={sub.status}>
                        {sub.status === 'active' && <CheckCircle size={14} />}
                        {sub.status === 'canceled' && <XCircle size={14} />}
                        {sub.status}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <div>
                        с {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '—'}
                      </div>
                      <div style={{ color: '#6b7280' }}>
                        до {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}
                      </div>
                    </Td>
                    <Td>{sub.auto_renew ? 'Да' : 'Нет'}</Td>
                    <Td>
                      <ActionsCell>
                        {sub.status !== 'canceled' ? (
                          <GhostButton
                            className="danger"
                            onClick={() => handleCancel(sub)}
                            disabled={isBusy}
                          >
                            <XCircle size={16} />
                            Отменить
                          </GhostButton>
                        ) : (
                          <GhostButton
                            onClick={() => handleReactivate(sub)}
                            disabled={isBusy}
                          >
                            <CheckCircle size={16} />
                            Активировать
                          </GhostButton>
                        )}
                      </ActionsCell>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Pagination>
              <PaginationButton
                onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                disabled={page === 0}
              >
                Назад
              </PaginationButton>
              <span>
                Страница {page + 1} • всего {total}
              </span>
              <PaginationButton onClick={() => setPage(prev => prev + 1)} disabled={!hasNext}>
                Далее
              </PaginationButton>
            </Pagination>
          </>
        )}
      </Panel>
    </Container>
  );
}

export default BillingSubscriptions;

